import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import GenericDialog from '../GenericDialog'
import RTORiskContent from './RTORiskContent'
import { getRiskRange, submitRiskRange } from 'src/lib/core/api-methods'
import { errorToast } from 'src/lib/utils/toasters'
import { useActivityLogs } from 'src/lib/hooks/useActivityLogs'
import { ActivityLogPayloadType } from 'src/lib/types/activityLogTypes'
import useGTM from 'src/lib/hooks/useGTM'

interface RTOSuiteSetupDialogProps {
  setOpen: Dispatch<SetStateAction<boolean>>
  open: boolean
  onRiskSetupSuccess?: () => void
  activityLogPayload?: ActivityLogPayloadType
}

const RTOSuiteSetupDialog = ({ onRiskSetupSuccess, open, setOpen, activityLogPayload }: RTOSuiteSetupDialogProps) => {
  const theme = useTheme()
  const [loading, setLoading] = useState(false)
  const [sendTags, setSendTags] = useState(true)
  const riskValuesRef = useRef<number[]>([40, 55])
  const [suggestedRanges, setSuggestedRanges] = useState<{
    min: number
    max: number
  }>({ min: 0, max: 0 })
  // For saving prev state of dialogue
  const actualValuesRef = useRef<{
    values: number[]
    sendTags: boolean
  }>({ values: [40, 55], sendTags: true })
  const { sendActivityLog } = useActivityLogs()
  const { sendGTMTrigger } = useGTM()
  useEffect(() => {
    const getRange = async () => {
      const {
        data
      }: {
        data: {
          min: number
          max: number
          send_tags_to_downstream: boolean
          suggested_min: number
          suggested_max: number
        }
      } = await getRiskRange()
      riskValuesRef.current = [data?.min ?? 40, data?.max ?? 55]
      actualValuesRef.current = {
        values: [data?.min ?? 40, data?.max ?? 55],
        sendTags: data?.send_tags_to_downstream
      }
      setSendTags(data?.send_tags_to_downstream)
      setSuggestedRanges({
        min: data?.suggested_min ?? 0,
        max: data?.suggested_max ?? 0
      })
    }
    getRange()
  }, [])

  const submitRiskValues = async () => {
    try {
      if (!riskValuesRef.current) throw new Error('No risk values set')
      setLoading(true)
      await submitRiskRange({
        min: riskValuesRef.current[0],
        max: riskValuesRef.current[1],
        send_tags_to_downstream: sendTags
      })
      activityLogPayload && sendActivityLog(activityLogPayload)
      onRiskSetupSuccess?.()
      sendGTMTrigger('rto_suite_risk_setup_edited', 'edit')
      setLoading(false)
      setOpen(false)
      actualValuesRef.current.values = [...riskValuesRef.current]
      actualValuesRef.current.sendTags = sendTags
    } catch (err) {
      errorToast('Something went wrong getting the risk ranges')
      setLoading(false)
    }
  }

  const resetStateOnClose = () => {
    setOpen(false)
    riskValuesRef.current = [...actualValuesRef.current.values]
    setSendTags(actualValuesRef.current.sendTags)
  }

  return (
    <GenericDialog
      open={open}
      loading={loading}
      onSave={submitRiskValues}
      title='RTO risk setup'
      onClose={resetStateOnClose}
      onCancel={resetStateOnClose}
      dialogProps={{
        maxWidth: false,
        PaperProps: {
          style: { borderRadius: 16 }
        }
      }}
      dialogTitleProps={{
        fontSize: '20px',
        sx: {
          padding: '1rem',
          fontWeight: 500,
          color: theme.palette.text.primary,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }
      }}
      saveButtonProps={{
        size: 'small',
        variant: 'contained',
        sx: {
          fontSize: 16,
          outline: 'none',
          backgroundColor: 'var(--flo-carbon-color)',
          '&:hover': {
            backgroundColor: 'var(--flo-carbon-color)',
            boxShadow: 'none'
          }
        }
      }}
    >
      <RTORiskContent
        sendTags={sendTags}
        setSendTags={setSendTags}
        riskValuesRef={riskValuesRef}
        suggestedRanges={suggestedRanges}
      />
    </GenericDialog>
  )
}

export default RTOSuiteSetupDialog
